import { FC, Fragment, useEffect, useMemo, useRef, useState } from 'react';

import TierComparison from './TierComparison';
import TestimonialCards from './TestimonialCards';
import MiniLeagueWinsTicker from './MiniLeagueWinsTicker';
import { getPlanIdForTier, resolvePlanPricing } from './utils/paywall-pricing';
import CtaButton from '@components/quiz/shared/CtaButton';
import StepHeader from '@components/quiz/shared/StepHeader';
import TrustpilotRating from '@components/quiz/shared/TrustpilotRating';
import AsSeenOnStrip from '@components/quiz/shared/AsSeenOnStrip';
import PromiseBadge from '@components/quiz/ReadinessSteps/shared/PromiseBadge';
import { interpolateStepTemplate } from '@components/quiz/ReadinessSteps/template-utils';
import { useAuth0User } from '@hooks/useAuth0User';
import { useGeoCurrency } from '@hooks/useGeoCurrency';
import { useMarketingCopy } from '@hooks/useMarketingCopy';
import { useSubscriptionPlans } from '@hooks/useSubscriptionPlans';
import {
  getCheckoutPath,
  getCleverTapMembershipTier,
  getCleverTapTierName
} from '@lib/utils';
import {
  CleverTap,
  ClevertapEvents,
  ClevertapEventProperties
} from '@lib/clevertap';
import { CHECKOUT_RETURN_PATHS } from '@lib/checkout/return-paths';
import { resolveArmCoupon } from '@lib/checkout/arm-coupon';
import {
  AnalyticsEvents,
  captureAnalyticsEvent,
  toAnalyticsMembershipTier
} from '@lib/analytics/events';
import { captureAnalyticsEventOncePerPage } from '@lib/analytics/once-per-page';
import { buildSanityImageUrl } from '@lib/sanity/image';
import type {
  PaywallPlanKey,
  SanityPaywallBlock,
  SanityQuizTheme
} from '@lib/sanity/types';
import type { QuizState } from '@lib/page-utils/Quiz';
import type { PaywallContext } from 'types/paywall';

type PaywallBlockProps = SanityPaywallBlock & {
  theme?: SanityQuizTheme;
  quizState: QuizState;
  templateKey: string;
  /**
   * The §8.0 funnel context payload. Null/undefined for direct traffic, where
   * the generic fallback copy is used instead of the funnel-specific headline.
   */
  paywallContext?: PaywallContext | null;
  /**
   * Pre-selects the tier checking out this plan, overriding the recommended
   * default. Used by landing pages for the ?plan= URL param; quiz pages omit
   * it and keep the recommended-tier default.
   */
  preselectedPlan?: PaywallPlanKey;
  /**
   * Email captured earlier in the funnel, forwarded to Auth0 as login_hint
   * so the signup screen arrives with the email field prefilled.
   */
  loginHintEmail?: string;
  /**
   * Forces the Auth0 signup screen to email/password only, hiding the
   * Google/Apple buttons so funnel users are more likely to register with
   * the email captured earlier. Funnel flow controllers set it; landing
   * pages omit it and keep the social buttons.
   */
  emailOnlySignup?: boolean;
  /**
   * Width of the centred content column. Landing pages pass a wider value for
   * desktop; quiz funnels omit it and keep the phone-width default.
   */
  containerClassName?: string;
  /**
   * Outer section classes. Defaults to the funnel spacing, whose large
   * bottom padding clears the fixed CTA bar when the paywall is the last
   * block. Landing pages, which render an FAQ and footer below the paywall,
   * pass a smaller bottom padding so there is no dead gap before the FAQ.
   */
  sectionClassName?: string;
  /**
   * Eyebrow shown when Sanity leaves the eyebrow blank. Defaults to the quiz
   * funnels' copy; landing pages pass neutral copy since no rating happened.
   */
  fallbackEyebrow?: string;
};

// The review carousel TrustBox proven on the homepage (components/
// reviews-carousel); defaults apply when Sanity leaves the fields unset.
const DEFAULT_TRUSTPILOT_CAROUSEL_TEMPLATE_ID = '54ad5defc6454f065c28af8b';
const TRUSTPILOT_CAROUSEL_HEIGHT = '240px';

// The analytics placement for the funnel-terminating paywall (FFH-2372).
const FUNNEL_PAYWALL_PLACEMENT = 'funnel_paywall';

const MINI_LEAGUE_NAME_ANSWER_KEY: Record<string, number> = {
  'gytr-pre-gw1': 2,
  'gytr-post-gw1': 2,
  'fpl-readiness': 1
};

const getMiniLeagueName = (
  templateKey: string,
  answers: Record<number, string>
): string | undefined => {
  const answerIndex = MINI_LEAGUE_NAME_ANSWER_KEY[templateKey];
  if (answerIndex === undefined) return undefined;
  return answers[answerIndex] || undefined;
};

const PaywallBlock: FC<PaywallBlockProps> = ({
  eyebrow,
  headline,
  subheadline,
  metricLabel,
  genericHeadline,
  genericSubheadline,
  tierCards,
  primaryCtaText,
  billedTodayTemplate,
  trialPeriodDays,
  trialBilledTodayTemplate,
  checkoutCoupon,
  ctaPromiseIcon,
  promiseEnabled,
  promiseIcon,
  promiseHeadline,
  promiseTermsLine,
  trustpilotTemplateId,
  trustpilotHeight,
  trustpilotWidth,
  asSeenOnLabel,
  asSeenOnItems,
  asSeenOnLogoHeight,
  asSeenOnMonochrome,
  testimonials,
  shouldUseTrustpilotCarousel,
  trustpilotCarouselTemplateId,
  trustpilotCarouselHeight,
  miniLeagueWins,
  templateKey,
  quizState,
  paywallContext,
  preselectedPlan,
  loginHintEmail,
  emailOnlySignup,
  containerClassName = 'mx-auto w-full max-w-md',
  sectionClassName = 'px-6 pb-44 pt-8',
  fallbackEyebrow = 'Your free rating'
}) => {
  const { user } = useAuth0User();
  const { currencyCode } = useGeoCurrency();
  const { promoDiscountPercent } = useMarketingCopy();

  // Live plan prices come from the same source as /join, so paywall pricing
  // never drifts from Stripe.
  const planKeys = useMemo(
    () => (tierCards ?? []).map((card) => card.plan),
    [tierCards]
  );
  const { plans } = useSubscriptionPlans(planKeys);

  // Pro (the recommended tier) is pre-selected; the single CTA checks out
  // whichever tier the user picks. A ?plan= override (preselectedPlan), when it
  // matches a tier, wins over the recommended default.
  const preselectedKey = preselectedPlan
    ? tierCards?.find((card) => card.plan === preselectedPlan)?._key
    : undefined;
  const initialSelectedKey =
    preselectedKey ??
    tierCards?.find((card) => card.isRecommended)?._key ??
    tierCards?.[0]?._key ??
    '';
  const [selectedKey, setSelectedKey] = useState(initialSelectedKey);
  const hasUserSelectedTier = useRef(false);

  const selectTier = (key: string) => {
    hasUserSelectedTier.current = true;
    setSelectedKey(key);
  };

  // On statically generated pages router.query is empty during the first
  // render, so preselectedPlan resolves after the selection state has already
  // initialised. Apply the late-arriving preselection unless the user has
  // already picked a tier themselves.
  useEffect(() => {
    if (preselectedKey && !hasUserSelectedTier.current) {
      setSelectedKey(preselectedKey);
    }
  }, [preselectedKey]);

  // League name from the funnel payload, falling back to the legacy
  // answers-based lookup for non-step funnels.
  const leagueName =
    paywallContext?.leagueName ??
    getMiniLeagueName(templateKey, quizState.answers);

  // FFH-2372: the funnel this paywall terminates, in analytics vocabulary.
  const analyticsFeature = templateKey?.startsWith('gytr')
    ? 'team_rating'
    : templateKey?.startsWith('readiness')
    ? 'mini_league_readiness'
    : templateKey ?? 'funnel';

  // FFH-2372: one view per page visit. Reaching this block IS the view — it
  // is the funnel's terminal step — so no visibility gating is needed. The
  // block remounts within a visit (tier reselects, context resolving), which
  // shipped ~4 views per session in prod; the once-per-page guard makes the
  // remounts silent.
  useEffect(() => {
    captureAnalyticsEventOncePerPage(
      AnalyticsEvents.paywallViewed,
      { placement: FUNNEL_PAYWALL_PLACEMENT, feature: analyticsFeature },
      `${FUNNEL_PAYWALL_PLACEMENT}|${analyticsFeature}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Funnel-specific copy interpolates the §8.0 payload tokens
  // ({{metric_value}}, {{metric_label}}, {{league_name}}); unknown tokens
  // resolve to ''. With no payload (direct traffic) the generic copy is used.
  const hasContext = paywallContext != null;
  const interpolate = (template: string): string =>
    interpolateStepTemplate(template, {
      metric_value: paywallContext?.metricValue ?? '',
      metric_label: metricLabel ?? '',
      league_name: leagueName ?? ''
    });

  // Prefer the first non-blank value: Sanity can store "" for a cleared
  // field, which `??` would treat as present and render blank.
  const firstNonBlank = (
    ...values: (string | undefined)[]
  ): string | undefined => values.find((value) => value?.trim());

  // Render the headline with the funnel metric value highlighted in primary
  // blue: interpolate the label/league tokens to text, but swap the metric
  // value for a sentinel so we can split on it and wrap the value in a span.
  const METRIC_SENTINEL = '@@METRIC@@';
  const metricValue = paywallContext?.metricValue ?? '';
  const headlineTemplate = hasContext
    ? headline
    : firstNonBlank(genericHeadline, headline) ?? headline;
  const headlineSegments = interpolateStepTemplate(headlineTemplate, {
    metric_value: METRIC_SENTINEL,
    metric_label: metricLabel ?? '',
    league_name: leagueName ?? ''
  }).split(METRIC_SENTINEL);

  const subheadlineCopy = hasContext
    ? subheadline
    : firstNonBlank(genericSubheadline, subheadline);
  const resolvedSubheadline = subheadlineCopy
    ? interpolate(subheadlineCopy)
    : undefined;

  // The selected tier drives the CTA target and the billed-today line.
  const selectedCard = tierCards?.find((card) => card._key === selectedKey);
  const selectedPlanId = selectedCard
    ? getPlanIdForTier(selectedCard.plan)
    : undefined;
  // The tier being purchased, for the checkout_started event. Guarded
  // explicitly rather than relying on the CTA's render condition: plan keys
  // are `<tier>_yearly`, so the leading segment maps to the canonical tier.
  const intendedTier = selectedCard?.plan
    ? getCleverTapTierName(selectedCard.plan.split('_')[0])
    : undefined;
  // The same tier in the lowercase PostHog vocabulary (FFH-2372); the
  // CleverTap name above is capitalised and must not be passed through.
  const analyticsIntendedTier = toAnalyticsMembershipTier(
    selectedCard?.plan?.split('_')[0]
  );
  // FFH-1742: a trial length set in Sanity flips this paywall to the trial
  // arm; checkout starts a Stripe free trial instead of billing today.
  const hasTrial = typeof trialPeriodDays === 'number' && trialPeriodDays > 0;

  // Funnel checkouts return to the funnel welcome page, not /thank-you.
  const checkoutHref =
    selectedPlanId != null
      ? getCheckoutPath(user, selectedPlanId, {
          returnPath: CHECKOUT_RETURN_PATHS.funnelWelcome,
          loginHintEmail,
          emailOnlySignup,
          trialPeriodDays: hasTrial ? trialPeriodDays : undefined,
          // FFH-1743: the discount arm's coupon, authored in Sanity. Undefined
          // on every other paywall, which then gets the API's default coupon.
          armCoupon: resolveArmCoupon(checkoutCoupon)
        })
      : undefined;
  const { annual } = selectedCard
    ? resolvePlanPricing(
        plans,
        selectedCard.plan,
        currencyCode,
        promoDiscountPercent
      )
    : { annual: null };
  // On the trial arm the billed-today line must not claim money is taken
  // today, so a trial-specific template replaces it. Defaulted (mirroring the
  // schema initialValue) so paywalls flipped to trial before the copy field
  // is populated never show the misleading upfront line. {{amount}} is the
  // discounted year-one charge, so the default names the renewal price
  // explicitly rather than implying that amount recurs.
  const DEFAULT_TRIAL_BILLED_TODAY_TEMPLATE =
    '{{trial_days}} days free, then {{amount}} for your first year. Renews at full price. Cancel anytime before the trial ends.';
  const billedTodayCopy = hasTrial
    ? firstNonBlank(trialBilledTodayTemplate) ??
      DEFAULT_TRIAL_BILLED_TODAY_TEMPLATE
    : billedTodayTemplate;

  // Split the billed-today line on the amount so the price can be bolded.
  const AMOUNT_SENTINEL = '@@AMOUNT@@';
  const billedTodaySegments =
    annual && billedTodayCopy
      ? interpolateStepTemplate(billedTodayCopy, {
          amount: AMOUNT_SENTINEL,
          trial_days: trialPeriodDays ?? ''
        }).split(AMOUNT_SENTINEL)
      : null;

  // Default the promise copy (mirrors the schema initialValues) so the badge
  // still renders on documents authored before these fields existed; editors
  // hide it via the promiseEnabled toggle.
  const resolvedPromiseHeadline =
    promiseHeadline?.trim() || 'Win your mini-league or your money back.';
  const resolvedPromiseTermsLine =
    promiseTermsLine?.trim() || 'New paying subscribers only. Terms apply.';
  const showPromise = promiseEnabled ?? true;

  return (
    <section className={`${sectionClassName} font-body`}>
      <div className={containerClassName}>
        {/* Top section: eyebrow + funnel-specific headline */}
        <StepHeader
          eyebrow={firstNonBlank(eyebrow, fallbackEyebrow)}
          heading={headlineSegments.map((segment, index) => (
            <Fragment key={index}>
              {segment}
              {index < headlineSegments.length - 1 && (
                <span className="text-primary-600">{metricValue}</span>
              )}
            </Fragment>
          ))}
          subtext={resolvedSubheadline || undefined}
          className="mb-6"
        />

        {/* Plan selector */}
        {tierCards && tierCards.length > 0 && (
          <div className="mb-3">
            <TierComparison
              tierCards={tierCards}
              plans={plans}
              currencyCode={currencyCode}
              discountPercent={promoDiscountPercent}
              selectedKey={selectedKey}
              onSelect={selectTier}
            />
          </div>
        )}

        {/* Billed-today line (price in bold) */}
        {checkoutHref && billedTodaySegments && (
          <p className="mb-8 text-center text-xs leading-normal text-neutral-500">
            {billedTodaySegments.map((segment, index) => (
              <Fragment key={index}>
                {segment}
                {index < billedTodaySegments.length - 1 && (
                  <span className="font-bold text-neutral-700">{annual}</span>
                )}
              </Fragment>
            ))}
          </p>
        )}

        {/* Money-back promise badge */}
        {showPromise && (
          <div className="mb-8">
            <PromiseBadge
              iconUrl={
                promiseIcon
                  ? buildSanityImageUrl(promiseIcon).height(96).url()
                  : undefined
              }
              headline={interpolate(resolvedPromiseHeadline)}
              termsLine={resolvedPromiseTermsLine}
              variant="light"
            />
          </div>
        )}

        {/* Trustpilot + as-seen-on (same strip as the email gate) */}
        <div className="mb-12 rounded-2xl border border-neutral-200 bg-white px-5 py-4">
          <TrustpilotRating
            templateId={trustpilotTemplateId}
            height={trustpilotHeight}
            width={trustpilotWidth}
          />
          {asSeenOnItems && asSeenOnItems.length > 0 && (
            <>
              <div className="my-4 border-t border-dashed border-neutral-200" />
              <AsSeenOnStrip
                items={asSeenOnItems}
                label={asSeenOnLabel}
                monochrome={asSeenOnMonochrome}
                logoHeight={asSeenOnLogoHeight}
              />
            </>
          )}
        </div>

        {/* Reviews: live Trustpilot carousel when toggled on in Sanity,
            otherwise the static testimonial cards */}
        {shouldUseTrustpilotCarousel ? (
          <div className="mb-12">
            <TrustpilotRating
              templateId={
                trustpilotCarouselTemplateId ||
                DEFAULT_TRUSTPILOT_CAROUSEL_TEMPLATE_ID
              }
              height={trustpilotCarouselHeight || TRUSTPILOT_CAROUSEL_HEIGHT}
            />
          </div>
        ) : (
          testimonials &&
          testimonials.length > 0 && (
            <div className="mb-12">
              <TestimonialCards testimonials={testimonials} />
            </div>
          )
        )}

        {/* Mini-league wins ticker */}
        {miniLeagueWins && miniLeagueWins.length > 0 && (
          <div className="mb-12">
            <MiniLeagueWinsTicker miniLeagueWins={miniLeagueWins} />
          </div>
        )}
      </div>

      {/* Primary CTA + promise line, anchored to the bottom of the viewport
          so it stays available however far the user scrolls. */}
      {checkoutHref && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 border-t border-neutral-200 bg-white px-6 pt-3 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]"
          style={{
            paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))'
          }}
        >
          <div className={containerClassName}>
            <CtaButton
              href={checkoutHref}
              onClick={() => {
                // checkout_started: clear purchase intent on the Sanity funnel
                // paywall, fired before Auth0/Stripe takes over (spec §5.3).
                // Mirrors JoinCard; `platform` is set by the CT SDK itself.
                CleverTap.event(ClevertapEvents.checkoutStarted, {
                  // Omit intended_tier rather than send `undefined` if the plan
                  // key can't be resolved — keeps malformed values out of CT.
                  ...(intendedTier && {
                    [ClevertapEventProperties.intendedTier]: intendedTier
                  }),
                  [ClevertapEventProperties.membershipTier]:
                    getCleverTapMembershipTier(user)
                });
                // FFH-2372: PostHog alongside CleverTap, never instead. This
                // CTA is both the upgrade click and the checkout start. All
                // funnel plans are yearly (`<tier>_yearly`).
                captureAnalyticsEvent(AnalyticsEvents.upgradeCtaClicked, {
                  placement: FUNNEL_PAYWALL_PLACEMENT,
                  intended_tier: analyticsIntendedTier
                });
                captureAnalyticsEvent(AnalyticsEvents.checkoutStarted, {
                  intended_tier: analyticsIntendedTier,
                  billing_period: 'annual',
                  coupon: resolveArmCoupon(checkoutCoupon) ?? null
                });
              }}
            >
              {primaryCtaText ?? 'Start winning now'}
            </CtaButton>
            {showPromise && (
              <div className="mt-2 flex items-center justify-center gap-1.5">
                {ctaPromiseIcon && (
                  <img
                    src={buildSanityImageUrl(ctaPromiseIcon).height(32).url()}
                    alt=""
                    aria-hidden="true"
                    width={16}
                    height={16}
                    className="h-4 w-4 shrink-0"
                  />
                )}
                <span className="font-body text-sm font-bold text-primary-600">
                  {interpolate(resolvedPromiseHeadline)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default PaywallBlock;
